import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const { customer, token } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [user, setUser] = useState(null);
  
  const { password, confirmPassword } = formData;
  
  // Verify token when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetch('/api/password-reset/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token,
            customerName: customer
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.msg || 'Invalid or expired reset link');
        }
        
        if (data.valid) {
          setTokenValid(true);
          setUser(data.user);
        } else {
          setError('Invalid or expired reset link');
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setError(err.message || 'Invalid or expired reset link');
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token, customer]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    
    // Clear previous errors
    setError('');
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch('/api/password-reset/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          customerName: customer,
          password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.msg || 'Error resetting password');
      }
      
      setSuccess(true);
      toast.success('Password reset successful');
      
      // Clear form
      setFormData({
        password: '',
        confirmPassword: ''
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Error resetting password');
    } finally {
      setLoading(false);
    }
  };
  
  // Render loading state
  if (loading && !error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Body className="text-center py-5">
                <div className="mb-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <p>Verifying your reset link...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Body className="text-center py-5">
                <div className="mb-4 text-danger">
                  <i className="bi bi-exclamation-circle" style={{ fontSize: '3rem' }}></i>
                </div>
                <h4 className="mb-3">Password Reset Failed</h4>
                <p className="mb-4">{error}</p>
                <p>
                  This link may have expired or already been used. 
                  Please contact your administrator to request a new password reset link.
                </p>
                <div className="mt-4">
                  <Button as={Link} to="/login" variant="primary">
                    Return to Login
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
  
  // Render success state
  if (success) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Body className="text-center py-5">
                <div className="mb-4 text-success">
                  <i className="bi bi-check-circle" style={{ fontSize: '3rem' }}></i>
                </div>
                <h4 className="mb-3">Password Reset Successful</h4>
                <p className="mb-4">Your password has been successfully reset.</p>
                <p>You will be redirected to the login page shortly.</p>
                <div className="mt-4">
                  <Button as={Link} to="/login" variant="primary">
                    Go to Login
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
  
  // Render reset form
  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h3 className="text-center mb-4">Reset Your Password</h3>
              
              {user && (
                <Alert variant="info">
                  <p className="mb-0">
                    <strong>Username:</strong> {user.username}
                    {user.firstName && user.lastName && (
                      <span> ({user.firstName} {user.lastName})</span>
                    )}
                  </p>
                </Alert>
              )}
              
              <Form onSubmit={onSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={password}
                    onChange={onChange}
                    required
                    minLength="6"
                    placeholder="Enter new password"
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 6 characters
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={onChange}
                    required
                    minLength="6"
                    placeholder="Confirm new password"
                  />
                </Form.Group>
                
                <div className="d-grid">
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
          
          <div className="text-center mt-3">
            <Link to="/login">Back to Login</Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
